import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MapPin, Users, Award, Globe, Shield, CheckCircle, Mail, Phone, Star, Clock, CreditCard, Sparkles, Heart, Briefcase, Calendar, Coffee, Camera } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AboutUs = () => {
  const { t } = useTranslation();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1516426122078-c23e76319801"
            alt="Uganda landscape"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Link to="/" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <div className="inline-flex items-center bg-ta-green/20 rounded-full px-2.5 py-0.5 text-xs text-ta-green mb-4 backdrop-blur-sm">
              <Award className="h-3 w-3 mr-1" />
              Est. 2022
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold mb-4">
              Discover Uganda's Beauty
            </h1>
            <p className="text-lg text-white/80">
              Connecting travelers with authentic experiences and local guides in the Pearl of Africa
            </p>
          </div>
        </div>
      </section>
      
      {/* Why Book with TravelCord Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Why Book with TravelCord
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Connecting travelers with authentic experiences and local guides in Uganda
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div 
              className="group relative bg-white p-6 rounded-xl border border-gray-100 hover:border-ta-green/20 transition-all duration-300 hover:shadow-lg hover:shadow-ta-green/5"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-ta-green/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              
              <div className="relative">
                <div className="w-12 h-12 bg-ta-green/10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="h-6 w-6 text-ta-green" />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Verified Local Guides
                </h3>
                
                <p className="text-gray-600 mb-4">
                  All our guides are carefully vetted and certified to ensure you get the best experience.
                </p>

                <div className="text-sm font-medium text-ta-green">
                  100+ guides
                </div>
              </div>
            </div>
            
            <div 
              className="group relative bg-white p-6 rounded-xl border border-gray-100 hover:border-ta-green/20 transition-all duration-300 hover:shadow-lg hover:shadow-ta-green/5"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-ta-green/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              
              <div className="relative">
                <div className="w-12 h-12 bg-ta-green/10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Clock className="h-6 w-6 text-ta-green" />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Best Price Guarantee
                </h3>
                
                <p className="text-gray-600 mb-4">
                  Find a better price? We will match it and give you an extra 10% off your next booking.
                </p>

                <div className="text-sm font-medium text-ta-green">
                  4.8/5 rating
                </div>
              </div>
            </div>
            
            <div 
              className="group relative bg-white p-6 rounded-xl border border-gray-100 hover:border-ta-green/20 transition-all duration-300 hover:shadow-lg hover:shadow-ta-green/5"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-ta-green/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              
              <div className="relative">
                <div className="w-12 h-12 bg-ta-green/10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Clock className="h-6 w-6 text-ta-green" />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Flexible Booking
                </h3>
                
                <p className="text-gray-600 mb-4">
                  Free cancellation up to 24 hours before your experience. No questions asked.
                </p>

                <div className="text-sm font-medium text-ta-green">
                  50k+ bookings
                </div>
              </div>
            </div>
            
            <div 
              className="group relative bg-white p-6 rounded-xl border border-gray-100 hover:border-ta-green/20 transition-all duration-300 hover:shadow-lg hover:shadow-ta-green/5"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-ta-green/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              
              <div className="relative">
                <div className="w-12 h-12 bg-ta-green/10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Users className="h-6 w-6 text-ta-green" />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Local Expertise
                </h3>
                
                <p className="text-gray-600 mb-4">
                  Get authentic insights and hidden gems from guides who know Uganda best.
                </p>

                <div className="text-sm font-medium text-ta-green">
                  95% satisfaction
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 bg-gray-50 rounded-2xl p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Easy Booking Process
                </h3>
                <ol className="space-y-4">
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-ta-green text-white rounded-full flex items-center justify-center text-sm font-medium">1</span>
                    <span className="ml-3 text-gray-600">Browse experiences and select your preferred date</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-ta-green text-white rounded-full flex items-center justify-center text-sm font-medium">2</span>
                    <span className="ml-3 text-gray-600">Choose your group size and any special requirements</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-ta-green text-white rounded-full flex items-center justify-center text-sm font-medium">3</span>
                    <span className="ml-3 text-gray-600">Secure your booking with our easy payment system</span>
                  </li>
                </ol>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Quality Assurance
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <Shield className="h-5 w-5 text-ta-green mr-2" />
                    <span className="text-gray-600">Licensed and insured guides</span>
                  </li>
                  <li className="flex items-center">
                    <Clock className="h-5 w-5 text-ta-green mr-2" />
                    <span className="text-gray-600">Regular quality checks</span>
                  </li>
                  <li className="flex items-center">
                    <Users className="h-5 w-5 text-ta-green mr-2" />
                    <span className="text-gray-600">Small groups for better experience</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Customer Support
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <Clock className="h-5 w-5 text-ta-green mr-2" />
                    <span className="text-gray-600">24/7 customer assistance</span>
                  </li>
                  <li className="flex items-center">
                    <CreditCard className="h-5 w-5 text-ta-green mr-2" />
                    <span className="text-gray-600">Secure payment protection</span>
                  </li>
                  <li className="flex items-center">
                    <Sparkles className="h-5 w-5 text-ta-green mr-2" />
                    <span className="text-gray-600">Satisfaction guaranteed</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Story Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center bg-ta-green/10 rounded-full px-2.5 py-0.5 text-xs text-ta-green">
                <Globe className="h-3 w-3 mr-1" />
                Our Story
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                Connecting Travelers with 
                <span className="text-ta-green"> Local Expertise</span>
              </h2>
              
              <div className="prose prose-lg text-gray-600">
                <p>
                  TravelCord was born from a passion for Uganda's breathtaking landscapes, rich cultural heritage, and the desire to showcase the country's beauty through the eyes of those who know it best – local guides.
                </p>
                <p>
                  Founded in 2022 by a team of travel enthusiasts and tourism professionals, we recognized a gap between international travelers seeking authentic experiences and the incredible local guides who could provide them.
                </p>
                <p>
                  Our platform bridges this gap by connecting travelers directly with verified local guides who offer personalized experiences across Uganda – from gorilla trekking in Bwindi and wildlife safaris in Queen Elizabeth National Park to cultural immersions in local communities and urban adventures in Kampala.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-ta-green/20 to-transparent rounded-2xl transform -rotate-3 scale-105"></div>
              <img 
                src="https://imgur.com/Qp7FZLw.jpg"
                alt="Gorilla trekking in Uganda"
                className="relative rounded-lg shadow-xl transform hover:scale-[1.02] transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Values Section */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center bg-ta-green/10 rounded-full px-2.5 py-0.5 text-xs text-ta-green mb-4">
              <Shield className="h-3 w-3 mr-1" />
              Our Values
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Core Principles that Guide Us
            </h2>
            
            <p className="text-gray-600">
              These fundamental values shape everything we do at TravelCord, from how we select our guides to how we design our experiences.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group relative bg-white p-6 rounded-xl border border-gray-200 hover:border-ta-green/20 transition-all duration-300 hover:shadow-lg hover:shadow-ta-green/5">
              <div className="absolute inset-0 bg-gradient-to-br from-ta-green/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              
              <div className="relative">
                <div className="w-12 h-12 bg-ta-green/10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Globe className="h-6 w-6 text-ta-green" />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Authenticity
                </h3>
                
                <p className="text-gray-600">
                  We prioritize genuine, non-commercialized experiences that showcase the real Uganda. Our guides share their personal stories and cultural insights.
                </p>
              </div>
            </div>
            
            <div className="group relative bg-white p-6 rounded-xl border border-gray-200 hover:border-ta-green/20 transition-all duration-300 hover:shadow-lg hover:shadow-ta-green/5">
              <div className="absolute inset-0 bg-gradient-to-br from-ta-green/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              
              <div className="relative">
                <div className="w-12 h-12 bg-ta-green/10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Users className="h-6 w-6 text-ta-green" />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Community Impact
                </h3>
                
                <p className="text-gray-600">
                  We believe tourism should benefit local communities. We work with guides from diverse backgrounds and support community-based tourism initiatives.
                </p>
              </div>
            </div>
            
            <div className="group relative bg-white p-6 rounded-xl border border-gray-200 hover:border-ta-green/20 transition-all duration-300 hover:shadow-lg hover:shadow-ta-green/5">
              <div className="absolute inset-0 bg-gradient-to-br from-ta-green/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              
              <div className="relative">
                <div className="w-12 h-12 bg-ta-green/10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="h-6 w-6 text-ta-green" />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Sustainability
                </h3>
                
                <p className="text-gray-600">
                  We promote responsible tourism practices that protect Uganda's natural environment and cultural heritage for future generations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Approach Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center bg-ta-green/10 rounded-full px-2.5 py-0.5 text-xs text-ta-green">
                <Briefcase className="h-3 w-3 mr-1" />
                Our Approach
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                How We Create <span className="text-ta-green">Unforgettable Experiences</span>
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-ta-green/10 rounded-lg flex items-center justify-center mt-1">
                    <Calendar className="h-4 w-4 text-ta-green" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Personalized Planning</h3>
                    <p className="text-gray-600">We work closely with each traveler to understand their interests, preferences, and travel style to create tailored experiences.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-ta-green/10 rounded-lg flex items-center justify-center mt-1">
                    <Users className="h-4 w-4 text-ta-green" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Expert Guide Matching</h3>
                    <p className="text-gray-600">We match travelers with guides who have specific expertise in their areas of interest, ensuring a deeper, more meaningful experience.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-ta-green/10 rounded-lg flex items-center justify-center mt-1">
                    <Heart className="h-4 w-4 text-ta-green" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Community Connection</h3>
                    <p className="text-gray-600">We create opportunities for meaningful cultural exchange between travelers and local communities.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-ta-green/10 rounded-lg flex items-center justify-center mt-1">
                    <Coffee className="h-4 w-4 text-ta-green" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Authentic Experiences</h3>
                    <p className="text-gray-600">We go beyond tourist attractions to provide genuine, off-the-beaten-path experiences that showcase the real Uganda.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img 
                  src="https://imgur.com/LUalhrs.jpg"
                  alt="Gorilla trekking"
                  className="rounded-lg shadow-md h-40 w-full object-cover"
                />
                <img 
                  src="https://imgur.com/5v6lVl8.jpg"
                  alt="Cultural experience"
                  className="rounded-lg shadow-md h-56 w-full object-cover"
                />
              </div>
              <div className="space-y-4 pt-8">
                <img 
                  src="https://imgur.com/WqH4Bly.jpg"
                  alt="Safari experience"
                  className="rounded-lg shadow-md h-56 w-full object-cover"
                />
                <img 
                  src="https://imgur.com/4kwMnNu.jpg"
                  alt="Food experience"
                  className="rounded-lg shadow-md h-40 w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Commitment Section */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center bg-ta-green/10 rounded-full px-2.5 py-0.5 text-xs text-ta-green mb-4">
              <Heart className="h-3 w-3 mr-1" />
              Our Commitment
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Responsible Tourism
            </h2>
            
            <p className="text-gray-600 max-w-2xl mx-auto">
              We're committed to sustainable practices that benefit local communities and protect Uganda's natural environment
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-ta-green/10 rounded-lg flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-ta-green" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Community Support</h3>
              <p className="text-gray-600">
                We partner with local communities to ensure tourism benefits those who call Uganda home. A portion of every booking goes directly to community development projects.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-ta-green/10 rounded-lg flex items-center justify-center mb-4">
                <Camera className="h-6 w-6 text-ta-green" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Wildlife Conservation</h3>
              <p className="text-gray-600">
                We support conservation efforts to protect Uganda's incredible biodiversity, including endangered species like mountain gorillas and chimpanzees.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-ta-green/10 rounded-lg flex items-center justify-center mb-4">
                <Globe className="h-6 w-6 text-ta-green" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Environmental Practices</h3>
              <p className="text-gray-600">
                We implement eco-friendly practices in all our operations and educate travelers on how to minimize their environmental impact while exploring Uganda.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Contact Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="inline-flex items-center bg-ta-green/10 rounded-full px-2.5 py-0.5 text-xs text-ta-green mb-4">
                <Mail className="h-3 w-3 mr-1" />
                Contact Us
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Get in Touch
              </h2>
              
              <p className="text-gray-600 mb-8">
                Have questions about our services or want to learn more about becoming a guide? We'd love to hear from you.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <MapPin className="h-5 w-5 text-ta-green mr-3 mt-1" />
                  <div>
                    <h3 className="font-medium text-gray-900">Address</h3>
                    <p className="text-gray-600">Ntinda, Kampala, Uganda</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Phone className="h-5 w-5 text-ta-green mr-3 mt-1" />
                  <div>
                    <h3 className="font-medium text-gray-900">Phone</h3>
                    <p className="text-gray-600">+256766830194</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Mail className="h-5 w-5 text-ta-green mr-3 mt-1" />
                  <div>
                    <h3 className="font-medium text-gray-900">Email</h3>
                    <p className="text-gray-600">info@travelcord.guide</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Links</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link 
                  to="/guide-application"
                  className="flex items-center p-3 bg-white rounded-lg hover:shadow-md transition-shadow group"
                >
                  <div className="w-10 h-10 bg-ta-green/10 rounded-lg flex items-center justify-center mr-3">
                    <Users className="h-5 w-5 text-ta-green" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 group-hover:text-ta-green transition-colors">Become a Guide</h4>
                    <p className="text-sm text-gray-600">Join our community</p>
                  </div>
                </Link>
                
                <Link 
                  to="/privacy-policy"
                  className="flex items-center p-3 bg-white rounded-lg hover:shadow-md transition-shadow group"
                >
                  <div className="w-10 h-10 bg-ta-green/10 rounded-lg flex items-center justify-center mr-3">
                    <Shield className="h-5 w-5 text-ta-green" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 group-hover:text-ta-green transition-colors">Privacy Policy</h4>
                    <p className="text-sm text-gray-600">Learn more</p>
                  </div>
                </Link>
                
                <Link 
                  to="/terms-of-service"
                  className="flex items-center p-3 bg-white rounded-lg hover:shadow-md transition-shadow group"
                >
                  <div className="w-10 h-10 bg-ta-green/10 rounded-lg flex items-center justify-center mr-3">
                    <CheckCircle className="h-5 w-5 text-ta-green" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 group-hover:text-ta-green transition-colors">Terms of Service</h4>
                    <p className="text-sm text-gray-600">Read our terms</p>
                  </div>
                </Link>
                
                <Link 
                  to="/cookie-policy"
                  className="flex items-center p-3 bg-white rounded-lg hover:shadow-md transition-shadow group"
                >
                  <div className="w-10 h-10 bg-ta-green/10 rounded-lg flex items-center justify-center mr-3">
                    <Globe className="h-5 w-5 text-ta-green" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 group-hover:text-ta-green transition-colors">Cookie Policy</h4>
                    <p className="text-sm text-gray-600">Cookie information</p>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default AboutUs;